import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { EmptyLinkedEntitiesState } from 'src/Components/EntityLinks/EmptyLinkedEntitiesState';
import { LinkedEntityGroup } from 'src/Components/EntityLinks/LinkedEntityGroup';
import { LinkEntityModal } from 'src/Components/EntityLinks/LinkEntityModal';
import {
  ENTITY_TYPE_ORDER,
  GROUP_ORDER,
  groupKeyOf,
  LinkGroupKey,
  MESSAGE_GROUP,
} from 'src/Components/EntityLinks/utils/entityLinkConstants';
import { getLinkedEntityRoute } from 'src/Components/EntityLinks/utils/entityLinkNavigation';
import { extendLinkTrail, readLinkTrail } from 'src/Components/EntityLinks/utils/linkTrail';
import { DocumentViewerModal } from 'src/Components/Modals/DocumentViewerModal';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import useFetchLinkedEntities from 'src/Hooks/Queries/useFetchLinkedEntities';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { LinkableEntityType, LinkedEntityDto, LinkOrigin } from 'src/Services/API/EntityLinks.api';
import { VideoDto } from 'src/Services/API/generated';
import {
  trackLinkedEntitiesSectionViewed,
  trackLinkedEntityClicked,
} from 'src/Services/Mixpanel/events';

import styles from './LinkedEntitiesSection.module.scss';
import { Props } from './LinkedEntitiesSection.types';

const sortItems = (items: LinkedEntityDto[]): LinkedEntityDto[] =>
  [...items].sort((a, b) => {
    if (a.linkOrigin !== b.linkOrigin) {
      return a.linkOrigin === LinkOrigin.Business ? -1 : 1;
    }
    return a.title.localeCompare(b.title);
  });

const defaultTargetTypeFor = (sourceType: LinkableEntityType): LinkableEntityType =>
  ENTITY_TYPE_ORDER.find(type => type !== LinkableEntityType.VirtualFile && type !== sourceType)!;

// Groupes rendus même vides, par type de fiche source. Sur un Projet, le groupe Actions
// doit toujours offrir un point d'entrée : sans lui, le lien Projet -> Action est
// invisible tant qu'aucune action n'est déjà rattachée.
const ALWAYS_VISIBLE_GROUPS_BY_SOURCE: Partial<Record<LinkableEntityType, LinkableEntityType[]>> = {
  [LinkableEntityType.Project]: [LinkableEntityType.Task],
};

const LinkedEntitiesSection = ({ entityType, entityId, entityTitle }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useFetchLinkedEntities(entityType, entityId);
  const [linkModalTargetType, setLinkModalTargetType] = useState<LinkableEntityType | null>(null);
  const [viewerFile, setViewerFile] = useState<{
    url: string;
    name: string;
    video?: VideoDto | null;
  } | null>(null);

  const { mutate: fetchFileInfo } = useFetchStorageItemInformation({
    onSuccess: fileInfo => {
      if (fileInfo.externalUrl) {
        window.open(fileInfo.externalUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      setViewerFile({
        url: fileInfo.urls?.previewLargeUrl ?? fileInfo.urls?.fileUrl ?? '',
        name: fileInfo.name,
        video: fileInfo.video,
      });
    },
  });

  useEffect(() => {
    if (data && data.length > 0) {
      trackLinkedEntitiesSectionViewed({
        source_entity_type: entityType,
        link_count: data.length,
        has_business_links: data.some(item => item.linkOrigin === LinkOrigin.Business),
        groups_count: new Set(data.map(item => item.entityType)).size,
      });
    }
    // Only re-fire when the data itself changes, not on every entityType identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { groups, excludeIdsByType } = useMemo(() => {
    const byGroup = new Map<LinkGroupKey, LinkedEntityDto[]>();
    const idsByType = new Map<LinkableEntityType, string[]>();
    for (const item of data ?? []) {
      // Never the source's own type, even if it links to a different instance of itself.
      if (item.entityType === entityType) continue;
      const key = groupKeyOf(item);
      byGroup.set(key, [...(byGroup.get(key) ?? []), item]);
      idsByType.set(item.entityType, [...(idsByType.get(item.entityType) ?? []), item.entityId]);
    }
    const alwaysVisible: LinkGroupKey[] = ALWAYS_VISIBLE_GROUPS_BY_SOURCE[entityType] ?? [];
    return {
      groups: GROUP_ORDER.filter(key => byGroup.has(key) || alwaysVisible.includes(key)).map(
        key => {
          const items = sortItems(byGroup.get(key) ?? []);
          return {
            key,
            items,
            progress:
              key === LinkableEntityType.Task
                ? {
                    done: items.filter(item => item.metadata?.status === 'DONE').length,
                    total: items.length,
                  }
                : undefined,
          };
        }
      ),
      excludeIdsByType: Object.fromEntries(idsByType.entries()) as Partial<
        Record<LinkableEntityType, string[]>
      >,
    };
  }, [data, entityType]);

  if (!data) return null;

  const handleItemClick = (item: LinkedEntityDto) => {
    if (!assetId) return;
    const trail = readLinkTrail(location.state);
    trackLinkedEntityClicked({
      source_entity_type: entityType,
      target_entity_type: item.entityType,
      link_origin: item.linkOrigin,
      trail_depth: trail.length,
    });

    const isExternalLink = item.metadata?.type === 'external_link';
    if (item.entityType === LinkableEntityType.VirtualFile && !isExternalLink) {
      fetchFileInfo(item.entityId);
      return;
    }

    navigate(getLinkedEntityRoute(assetId, item.entityType, item.entityId, item.metadata), {
      state: {
        linkTrail: extendLinkTrail(trail, {
          type: entityType,
          id: entityId,
          title: entityTitle ?? t('entityLinks.trail.previous', 'Fiche précédente'),
          path: `${location.pathname}${location.search}`,
        }),
      },
    });
  };

  return (
    <div className={styles.container}>
      {groups.length === 0 ? (
        <EmptyLinkedEntitiesState
          onAddClick={() => setLinkModalTargetType(defaultTargetTypeFor(entityType))}
        />
      ) : (
        groups.map(group => (
          <LinkedEntityGroup
            key={group.key}
            groupKey={group.key}
            items={group.items}
            progress={group.progress}
            onItemClick={handleItemClick}
            onAddClick={() =>
              setLinkModalTargetType(
                group.key === MESSAGE_GROUP ? LinkableEntityType.CommunityFeedItem : group.key
              )
            }
            sourceType={entityType}
            sourceId={entityId}
          />
        ))
      )}

      {linkModalTargetType && (
        <LinkEntityModal
          opened
          onClose={() => setLinkModalTargetType(null)}
          sourceType={entityType}
          sourceId={entityId}
          initialTargetType={linkModalTargetType}
          excludeIdsByType={excludeIdsByType}
        />
      )}

      <DocumentViewerModal
        open={!!viewerFile}
        fileUrl={viewerFile?.url ?? ''}
        fileName={viewerFile?.name ?? ''}
        video={viewerFile?.video}
        onClose={() => setViewerFile(null)}
      />
    </div>
  );
};

export default LinkedEntitiesSection;
